import React from 'react';
import { Home, Wifi, Coffee, Users, Clock, Volume2, Focus, Shield } from 'lucide-react';

const AdvantagesSection = () => {
  const advantages = [
    {
      icon: Focus,
      title: 'Zero Distractions',
      home: 'TV, family conversations, household chores constantly interrupt your study flow',
      studySpace: 'Complete silence policy with dedicated study zones designed for deep focus',
      color: 'blue'
    },
    {
      icon: Users,
      title: 'Motivating Environment',
      home: 'Isolated studying can feel demotivating and lonely',
      studySpace: 'Surrounded by goal-oriented students creating positive peer pressure',
      color: 'sky'
    },
    {
      icon: Clock,
      title: 'Structured Routine',
      home: 'Easy to procrastinate and lose track of time at home',
      studySpace: 'Fixed environment helps build consistent study habits and discipline',
      color: 'indigo'
    },
    {
      icon: Wifi,
      title: 'Professional Setup',
      home: 'Uncomfortable furniture, poor lighting, unreliable internet',
      studySpace: 'Ergonomic chairs, proper lighting, high-speed WiFi, power backup',
      color: 'blue'
    },
    {
      icon: Shield,
      title: 'Secure Space',
      home: 'No dedicated space for your study materials and books',
      studySpace: 'Personal locker for each desk to secure your belongings',
      color: 'sky'
    },
    {
      icon: Volume2,
      title: 'Peaceful Atmosphere',
      home: 'Neighborhood noise, doorbell rings, phone calls disrupting concentration',
      studySpace: 'Soundproof environment with strict no-calls, no-music policy',
      color: 'indigo'
    }
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      hover: 'hover:border-blue-400'
    },
    sky: {
      bg: 'bg-sky-50',
      icon: 'text-sky-600',
      border: 'border-sky-200',
      hover: 'hover:border-sky-400'
    },
    indigo: {
      bg: 'bg-indigo-50',
      icon: 'text-indigo-600',
      border: 'border-indigo-200',
      hover: 'hover:border-indigo-400'
    }
  };

  return (
    <div className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
            Why Choose Us
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Why Study at Adhyan Instead of Home?
          </h2>
          <p className="text-lg text-gray-600">
            Discover how a dedicated study space can transform your productivity and help you achieve your goals faster
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {advantages.map((advantage, index) => {
            const Icon = advantage.icon;
            const colors = colorClasses[advantage.color];
            
            return (
              <div
                key={index}
                className={`bg-white border-2 ${colors.border} ${colors.hover} rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
              >
                <div className={`${colors.bg} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={colors.icon} size={28} />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">{advantage.title}</h3>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="mt-1">
                      <Home size={18} className="text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-red-600 uppercase mb-1">At Home</p>
                      <p className="text-sm text-gray-600">{advantage.home}</p>
                    </div>
                  </div>
                  
                  <div className="h-px bg-gray-200"></div>
                  
                  <div className="flex gap-3">
                    <div className="mt-1">
                      <Shield size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-blue-600 uppercase mb-1">At Adhyan</p>
                      <p className="text-sm text-gray-800 font-medium">{advantage.studySpace}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
          <div className="relative z-10">
            <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Study Routine?</h3>
            <p className="text-blue-100 text-lg mb-6">
             Join successful students
            </p>
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-blue-700 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Book Your Desk Today
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvantagesSection;
